import { uniq } from "es-toolkit/array";
import type { z } from "zod/v4";
import { toFiniteNumber } from "@/lib/ai/visualization/shared/primitives";
import type { VizAuthoringIssue } from "@/lib/ai/visualization/types";
import type { VizSpec } from "@/lib/ai/visualization/views/compiled-spec";
import type { scatterPlotAuthoringSchema } from "@/lib/ai/visualization/views/scatter-plot/schema";
import type {
  BuildVizElementArgs,
  BuiltVizElement,
} from "@/lib/ai/visualization/views/shared/element-builder";
import {
  buildSpecValidationIssue,
  collectSeriesValues,
  pushMappedKeyIssues,
  pushRequiredTextIssue,
  pushSelectedKeyIssues,
  validateFieldRole,
} from "@/lib/ai/visualization/views/shared/spec-validation";
import type {
  ValidateVizSpecArgs,
  VizAuthoringCardDraft,
} from "@/lib/ai/visualization/views/shared/view-definition";

type ScatterPlotSpec = Extract<VizSpec, { type: "scatter-plot" }>;
type ScatterPlotAuthoring = z.infer<typeof scatterPlotAuthoringSchema>;

function toPlottableScatterNumber(value: unknown): number | null {
  return toFiniteNumber(value);
}

function toScatterSeriesValue(value: unknown): string {
  if (typeof value === "string") {
    const trimmed = value.trim();
    return trimmed.length > 0 ? trimmed : "Series";
  }
  if (typeof value === "number" && Number.isFinite(value)) {
    return String(value);
  }
  return "Series";
}

/**
 * scatter surface が quantitative axis、同一rowの点、series visibility contractを満たすか検証する。
 *
 * @remarks
 * 期間比較ではcurrent・previous・differenceが別fieldでも同じ要求metricを表す。
 * 選択済みX/Yが同じ`metricFamily`なら2変数比較にならないため、描画前に
 * `same-metric-family` issueを返す。異なるfamilyは既存のrow-level投影検証へ進む。
 * `zKey`を指定した場合は全描画点に有限値を要求し、欠損点を既定sizeで描いて
 * magnitude encodingがあるように誤認させない。欠損時は`unprojectable-binding`を返す。
 */
export function validateScatterPlotSpec(
  args: ValidateVizSpecArgs<ScatterPlotSpec>,
): VizAuthoringIssue[] {
  const issues: VizAuthoringIssue[] = [];
  validateFieldRole({
    issues,
    dataset: args.dataset,
    path: "spec.xKey",
    fieldName: args.spec.xKey,
    role: "metric",
  });
  validateFieldRole({
    issues,
    dataset: args.dataset,
    path: "spec.yKey",
    fieldName: args.spec.yKey,
    role: "metric",
  });

  if (args.spec.xKey === args.spec.yKey) {
    issues.push(
      buildSpecValidationIssue({
        path: "spec.yKey",
        code: "duplicate-axis-key",
        message: "must be different from spec.xKey",
        actual: args.spec.yKey,
      }),
    );
  }

  const xField = args.dataset.fields.find(
    (field) => field.name === args.spec.xKey,
  );
  const yField = args.dataset.fields.find(
    (field) => field.name === args.spec.yKey,
  );
  if (
    args.dataset.profile.compareMode === "period" &&
    args.spec.xKey !== args.spec.yKey &&
    xField?.metricFamily &&
    yField?.metricFamily &&
    xField.metricFamily === yField.metricFamily
  ) {
    issues.push(
      buildSpecValidationIssue({
        path: "spec.yKey",
        code: "same-metric-family",
        message:
          "period-comparison scatter axes must represent different metric families",
        expected: "metric family different from spec.xKey",
        actual: yField.metricFamily,
      }),
    );
  }

  const plottableAxisRows = args.dataset.rows.filter(
    (row) =>
      toPlottableScatterNumber(row[args.spec.xKey]) !== null &&
      toPlottableScatterNumber(row[args.spec.yKey]) !== null,
  );
  const hasPlottableAxisPair = plottableAxisRows.length >= 2;
  if (!hasPlottableAxisPair) {
    issues.push(
      buildSpecValidationIssue({
        path: "spec.xKey",
        code: "unprojectable-binding",
        message:
          "selected scatter axes must coexist with finite values on at least two rows",
        expected: "co-realized scatter metric pair across multiple rows",
        actual: `${args.spec.xKey}, ${args.spec.yKey}`,
      }),
    );
  }

  if (args.spec.zKey) {
    validateFieldRole({
      issues,
      dataset: args.dataset,
      path: "spec.zKey",
      fieldName: args.spec.zKey,
      role: "metric",
    });
    if (
      args.spec.zKey === args.spec.xKey ||
      args.spec.zKey === args.spec.yKey
    ) {
      issues.push(
        buildSpecValidationIssue({
          path: "spec.zKey",
          code: "duplicate-z-key",
          message: "must be different from spec.xKey and spec.yKey",
          actual: args.spec.zKey,
        }),
      );
    }
    if (
      plottableAxisRows.length > 0 &&
      plottableAxisRows.some(
        (row) => toPlottableScatterNumber(row[args.spec.zKey!]) === null,
      )
    ) {
      issues.push(
        buildSpecValidationIssue({
          path: "spec.zKey",
          code: "unprojectable-binding",
          message:
            "selected scatter size metric must be finite on every plotted X/Y row",
          expected: "co-realized finite size value for each plotted point",
          actual: args.spec.zKey,
        }),
      );
    }
  }

  const realizedSeriesKeys = args.spec.seriesByKey
    ? new Set(collectSeriesValues(args.dataset, args.spec.seriesByKey))
    : new Set<string>();
  pushRequiredTextIssue({
    issues,
    path: "spec.componentProps.xLabel",
    value: args.spec.componentProps?.xLabel ?? null,
    message:
      "xLabel is required because scatter axis labels are owned by the authoring output",
  });
  pushRequiredTextIssue({
    issues,
    path: "spec.componentProps.yLabel",
    value: args.spec.componentProps?.yLabel ?? null,
    message:
      "yLabel is required because scatter axis labels are owned by the authoring output",
  });
  if (args.spec.seriesByKey) {
    validateFieldRole({
      issues,
      dataset: args.dataset,
      path: "spec.seriesByKey",
      fieldName: args.spec.seriesByKey,
      role: "dimension",
    });
  }

  pushMappedKeyIssues({
    issues,
    path: "spec.componentProps.seriesLabels",
    entries: args.spec.componentProps?.seriesLabels,
    allowedKeys: realizedSeriesKeys,
    emptyAllowedMessage:
      "seriesLabels requires spec.seriesByKey with realized series values",
  });
  pushSelectedKeyIssues({
    issues,
    path: "spec.componentProps.defaultVisibleSeriesKeys",
    selectedKeys: args.spec.componentProps?.defaultVisibleSeriesKeys ?? [],
    allowedKeys: realizedSeriesKeys,
    emptyAllowedMessage:
      "defaultVisibleSeriesKeys requires spec.seriesByKey with realized series values",
  });

  return issues;
}

/**
 * structured authoring payload を `scatter-plot` 用の renderer-ready spec へ変換する。
 */
export function compileScatterPlotSpec(args: {
  datasetId: string;
  dataset: BuildVizElementArgs<ScatterPlotSpec>["dataset"];
  card: VizAuthoringCardDraft | null;
  view: ScatterPlotAuthoring;
}): ScatterPlotSpec {
  return {
    type: "scatter-plot",
    datasetId: args.datasetId,
    cardTitle: args.card?.title ?? null,
    xKey: args.view.xKey,
    yKey: args.view.yKey,
    zKey: args.view.zKey ?? null,
    seriesByKey: args.view.seriesByKey ?? null,
    componentProps: {
      xLabel: args.view.xLabel,
      yLabel: args.view.yLabel,
    },
  };
}

/**
 * scatter-plot 用の UI element を構築する。
 */
export function buildScatterPlotElement(
  args: BuildVizElementArgs<ScatterPlotSpec>,
): BuiltVizElement {
  const seriesLabels = args.spec.componentProps?.seriesLabels ?? {};
  const seriesValues =
    args.spec.seriesByKey && args.spec.seriesByKey.length > 0
      ? uniq(
          args.dataset.rows
            .filter((row) => {
              return (
                toPlottableScatterNumber(row[args.spec.xKey]) !== null &&
                toPlottableScatterNumber(row[args.spec.yKey]) !== null
              );
            })
            .map((row) => toScatterSeriesValue(row[args.spec.seriesByKey!])),
        )
      : [];

  return {
    elementKey: args.chartKey,
    element: {
      type: "ScatterPlot",
      props: {
        dataPath: args.dataPath,
        xKey: args.spec.xKey,
        yKey: args.spec.yKey,
        zKey: args.spec.zKey,
        seriesKey: args.spec.seriesByKey,
        series: seriesValues.map((value) => ({
          key: value,
          label: seriesLabels[value] ?? value,
          color: null,
        })),
        ...(args.spec.componentProps ?? {}),
      },
      children: [],
    },
  };
}
